import React, { useState, useEffect, createContext, useContext } from 'react';
import { Slack, MicrosoftTeams } from 'react-bootstrap-icons';
import { v4 as uuidv4 } from 'uuid';
// 
import ConfigService from '../../services/config.service';
import ProductSettingsService from '../../services/product.service';
import { AuthContext } from '../../services/auth.service';
import LoadingScreen from '../../components/elements/LoadingScreen';

import "./styles.scss";

import { defaultEventTemplates } from './defaultEventTemplates';
import { transformDataObject } from '../../components/elements/JsonObjTable';

import PageHeader from '../../components/elements/PageHeader';
import { SideMenu } from '../../components/layouts/MainLayout/SideMenu';
import { InteractivePanel } from "../../components/layouts/MainLayout/InteractivePanel";
import { EmailsNotifModal } from '../../components/layouts/MainLayout/SideMenu/EmailNotifModal';
import { SlackNotifModal } from '../../components/layouts/MainLayout/SideMenu/SlackNotifModal';
import { TeamsNotifModal } from '../../components/layouts/MainLayout/SideMenu/TeamsNotifModal';

import {
    ProductSettinsModal,
    DataLayerEventsModal,
    MonitoringModal,
} from "./configModals";

import ScenariosMenu from './ScenariosMenu/index';

import { useMessage } from "../../components/elements/MessageCenter";

const _ = require('lodash');

const generateUniqueId = () => uuidv4();

export const simplifyDLGEventJson = (template) => {

    const modifyRows = (rows) => {
        const newObj = {};

        if (Array.isArray(rows) && rows.length > 0) {

            rows.forEach((row) => {
                const key = row.key;
                const value = row.value;
                const valueType = row.type;

                if (valueType === "array") {

                    const newNestedObj = modifyRows(value);
                    const itemsList = [];

                    if (typeof newNestedObj === "object" && Object.keys(newNestedObj).length === 0) {
                        newObj[key] = itemsList;
                    } else {
                        itemsList.push(newNestedObj);
                        newObj[key] = itemsList;
                    }


                } else if (valueType === "object") {
                    const newNestedObj = modifyRows(value);
                    newObj[key] = newNestedObj;

                } else {
                    if (!row.exatclyMatch) {
                        newObj[key] = valueType;
                    } else {
                        newObj[key] = value;
                    }

                }
            });
        }

        return newObj;
    };

    const newList = modifyRows(template);
    return newList;
};


const escapeEvents = async (events = {}, mainEvents = {}) => {
    events?.events?.forEach((row) => {
        const eventTemplate = row?.main_templates;
        const simplifiedTemplate = simplifyDLGEventJson(eventTemplate);
        row.templates = simplifiedTemplate;
        delete row.main_templates;
    });
    const updEvents = encodeURIComponent(JSON.stringify(events, 2));

    mainEvents?.events?.forEach((row) => delete row.templates);
    const updMainEvents = encodeURIComponent(JSON.stringify(mainEvents, 2));

    return {
        events: updEvents,
        mainEvents: updMainEvents,
    }
}


// Save function
export const SaveConfigData = async (params) => {
    const {
        companyId,
        currentConfig,
        setCurrentConfig,
        configParams,
        modalSaveFunction,
        handlePanelClick,
        setLoadingActive,
        currentPage,
        setCurrentPage,
        setModalContentCodeControl,
        setSideMenuState,
        success,
        error,
    } = params;

    setLoadingActive(true);

    // Deep clone configParams.templates into events and mainEvents
    const events = JSON.parse(JSON.stringify(configParams?.templates || {}));
    const mainEvents = JSON.parse(JSON.stringify(configParams?.templates || {}));
    const transEvents = JSON.parse(JSON.stringify(configParams?.transaction_templates || {}));
    const mainTransEvents = JSON.parse(JSON.stringify(configParams?.transaction_templates || {}));

    const escapedEvents = await escapeEvents(events, mainEvents);
    const escapedTransactionEvents = await escapeEvents(transEvents, mainTransEvents);

    if (configParams?.templates) {
        configParams.templates = escapedEvents?.events;
        configParams.main_templates = escapedEvents?.mainEvents;
    }

    if (configParams?.transaction_templates) {
        configParams.transaction_templates = escapedTransactionEvents?.events;
        configParams.main_transaction_templates = escapedTransactionEvents?.mainEvents;
    }

    if (configParams?.scenarios) {
        configParams.scenarios = encodeURIComponent(JSON.stringify(configParams.scenarios, 2));
    }

    const configObj = { ...currentConfig };

    Object.keys(configParams).forEach(key => configObj[key] = configParams[key]);

    const resp = await modalSaveFunction({ configObj, companyId });
    const respData = await resp?.json() || resp;

    switch (respData?.result) {
        case true:
            success("Configuration data successfully saved!");
            setCurrentConfig(configObj);
            break;
        default:
            error();
    }

    switch (currentPage) {
        case "dl_events":
        case "monitoring":
            setModalContentCodeControl(Date.now());
            setSideMenuState(true);
            handlePanelClick(currentPage);
            break;
        default:
            setModalContentCodeControl(null);
            handlePanelClick("empty");
            setCurrentPage("empty");
            setSideMenuState(false);
    }

    setLoadingActive(false);
}

export const ProdSettingsContext = createContext();
export const DataLayerEventsContext = createContext();
export const MonitoringContext = createContext();
export const EmailsNotifContext = createContext();
export const SlackNotifContext = createContext();
export const TeamsNotifContext = createContext();

export const ScenariosContext = createContext();

const productDescLink = `https://docs.code-cube.io/datalayer-guard/overview/`;
const emailNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-email-notifications/`;
const slackNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-slack-notifications/`;
const teamsNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-teams-notifications/`;
const scrapedEventsDescLink = `https://docs.code-cube.io/datalayer-guard/overview/`;
const pageScenariosDescLink = `https://docs.code-cube.io/datalayer-guard/overview/`;
const tagMangerEventsDescLink = `https://docs.code-cube.io/datalayer-guard/events-tag-manager/`;


export const DataLayerGuardConfig = () => {
    const { companyId, companyInfo, adminUser } = useContext(AuthContext);
    const [currCompanyInfo, setCurrCompanyInfo] = useState(companyInfo);

    // Using popup messages
    const { success, warning, error } = useMessage();

    const [sideMenuState, setSideMenuState] = useState(false);
    const [currentModal, setCurrentModal] = useState(<></>);
    const [modalWidth, setModalWidth] = useState("narrow");
    const [currentPage, setCurrentPage] = useState("empty");
    const [modalContentCodeControl, setModalContentCodeControl] = useState(Date.now());

    // Drawer Menu
    const [scenariosMenuState, setScenariosMenuState] = useState(false);

    const [loadingActive, setLoadingActive] = useState(true);

    const [currentConfig, setCurrentConfig] = useState({});
    const [configParams, setConfigParams] = useState({});

    const handlePanelClick = (page) => {
        switch (page) {
            case "dl_events":
            case "monitoring":
                setModalWidth("wide");
                setSideMenuState(true);
                setCurrentModal(modalFunc[page]);
                break;
            case "scenarios":
                setScenariosMenuState(true);
                setSideMenuState(false);
                break;

            default:
                setModalWidth("narrow");
                setSideMenuState(true);
                setCurrentModal(modalFunc[page]);
        }

        setCurrentPage(page);
    };

    const [emailNotifAvailable, setEmailNotifAvailable] = useState(false);
    const [slackNotifAvailable, setSlackNotifAvailable] = useState(false);
    const [teamsNotifAvailable, setTeamsNotifAvailable] = useState(false);

    const [timezones, setTimezones] = useState([]);
    const [timezoneName, setTimezoneName] = useState("");
    const [curTimezone, setCurTimezone] = useState(null);
    const [dlgCheckFreq, setDlgCheckFreq] = useState([]);
    const [curCheckFreq, setCurCheckFreq] = useState(null);
    const [companyDomains, setCompanyDomains] = useState([]);

    const [dlEvents, setDlEvents] = useState([]);
    const [newTemplateVersionAvailable, setnewTemplateVersionAvailable] = useState(false);

    const [transactionEvents, setTransactionEvents] = useState([]);
    const [newTransTempVersionAvailable, setNewTransTempVersionAvailable] = useState(false);
    const [databaseName, setDatabaseName] = useState("");

    const [slackWebhook, setSlackWebhook] = useState("");

    // Reloading the page content only if the new company profile is different
    useEffect(() => _.isEqual(companyInfo, currCompanyInfo) ? undefined : setCurrCompanyInfo(companyInfo), [companyInfo]);


    // Add ecommerce event button functionality
    const handleTempsPushDefEcomObj = (eventName, idx, eventObj) =>
        pushDefaultEcommerceObject(eventName, idx, eventObj, dlEvents, setDlEvents);

    const handleTransTempsPushDefEcomObj = (eventName, idx, eventObj) =>
        pushDefaultEcommerceObject(eventName, idx, eventObj, transactionEvents, setTransactionEvents);

    const handleTempsCleanObj = (idx, eventObj) =>
        cleanJsonObject(idx, eventObj, dlEvents, setDlEvents);

    const handleTransTempsCleanObj = (idx, eventObj) =>
        cleanJsonObject(idx, eventObj, transactionEvents, setTransactionEvents);


    const pushDefaultEcommerceObject = (eventName = "page_load", idx, eventObj, events, setEvents) => {
        const newTemplates = [defaultEventTemplates[eventName]];

        eventObj.templates = JSON.stringify(newTemplates);
        eventObj.main_templates = transformDataObject(newTemplates);
        const newEventsList = [];

        events?.forEach(elem => {
            if (elem?.uniqueId !== idx) newEventsList.push(elem);
            else newEventsList.push(eventObj);
        });

        setEvents(newEventsList);

        return newTemplates;
    }

    // Clean JSON object
    const cleanJsonObject = (idx, eventObj, events = [], setEvents) => {
        eventObj.templates = "";
        eventObj.main_templates = "";
        const newEventsList = [];

        events?.forEach(elem => {
            if (elem?.uniqueId !== idx) newEventsList.push(elem);
            else newEventsList.push(eventObj);
        });

        setEvents(newEventsList);

        return [];
    }

    // Add event handle
    const handleTempEventAdd = () => handleEventObjectAdd(dlEvents, setDlEvents);
    const handleTransTempEventAdd = () => handleEventObjectAdd(transactionEvents, setTransactionEvents);

    const handleEventObjectAdd = (events = [], setEvents) => {
        const newEventUniqueId = generateUniqueId();

        const newEvent = {
            uniqueId: newEventUniqueId
        };
        const newEventsList = [...events, newEvent];

        setEvents(newEventsList);

        return {
            newEvent,
            newEventsList,
            newEventUniqueId,
        };
    }

    // Remove event handle
    const handleTempEventRemove = (idx) => handleEventObjectRemove(dlEvents, setDlEvents, idx);
    const handleTransTempEventRemove = (idx) => handleEventObjectRemove(transactionEvents, setTransactionEvents, idx);

    const handleEventObjectRemove = (events, setEvents, idx) => {
        const newEventsList = [...events]?.filter((elem) => elem?.uniqueId !== idx ? elem : undefined);
        setEvents(newEventsList);

        return newEventsList;
    }


    useEffect(() => {
        (async () => {
            setLoadingActive(true);

            setSideMenuState(false);

            const resp = await ConfigService.getDataLayerGuardConfigData(companyId);
            const respData = await resp.json();

            setCurrentConfig(respData?.config_data?.current_config);

            const configData = respData?.config_data || {};
            setSlackWebhook(configData?.slack_webhook || "");

            // Product settings data
            setTimezones(respData?.config_data?.timezones || []);
            setTimezoneName(respData?.config_data?.current_config.timezone_name || "");
            setCurTimezone(respData?.config_data?.current_config?.timezone || "");
            setDlgCheckFreq(respData?.config_data?.dlg_check_freq || "");
            setCurCheckFreq(respData?.config_data?.current_config?.check_frequency || "");

            // DataLayer Events
            const tempsStr = respData?.config_data?.current_config?.main_templates || "{}";
            const tempEvents = JSON.parse(decodeURIComponent(tempsStr))?.events;
            setDlEvents(tempEvents);

            setnewTemplateVersionAvailable(() => !!respData?.config_data?.current_config?.main_templates);
            setNewTransTempVersionAvailable(() => !!respData?.config_data?.current_config?.main_transaction_templates);

            // Monitoring via Tag Manager
            const transactionEventsStr = respData?.config_data?.current_config?.main_transaction_templates || "{}";
            const transEvents = JSON.parse(decodeURIComponent(transactionEventsStr))?.events;
            setTransactionEvents(transEvents);
            setDatabaseName(respData?.config_data?.current_config?.database_name || "");
            setCompanyDomains(respData?.config_data?.current_config?.company_domains || []);

            const subLevel = companyInfo?.datalayer_guard_subscription_model?.toLowerCase();
            const settings = await ProductSettingsService.getProductSettings();
            const subSettings = settings?.datalayer_guard_settings[subLevel];
            setEmailNotifAvailable(subSettings?.email_notif);
            setSlackNotifAvailable(subSettings?.slack_notif);
            setTeamsNotifAvailable(subSettings?.teams_notif);

            setLoadingActive(false);
        })();
    }, [currCompanyInfo]);

    const modalFunc = {
        "empty": <></>,
        "general":
            <ProdSettingsContext.Provider
                value={{
                    productDescLink,
                    timezones,
                    timezoneName,
                    curTimezone,
                    dlgCheckFreq,
                    curCheckFreq,
                    setCurTimezone,
                    setCurCheckFreq,
                    setConfigParams,
                    companyDomains,
                    setCompanyDomains
                }}>
                <ProductSettinsModal context={ProdSettingsContext} />
            </ProdSettingsContext.Provider>,
        "dl_events":
            <DataLayerEventsContext.Provider
                value={{
                    companyId,
                    scrapedEventsDescLink,
                    dlEvents,
                    setDlEvents,
                    setConfigParams,
                    companyDomains,
                    newTemplateVersionAvailable,
                    pushDefaultEcommerceObject: handleTempsPushDefEcomObj,
                    cleanJsonObject: handleTempsCleanObj,
                    handleEventAdd: handleTempEventAdd,
                    handleEventRemove: handleTempEventRemove,
                    error,
                }}>
                <DataLayerEventsModal context={DataLayerEventsContext} />
            </DataLayerEventsContext.Provider>,
        "monitoring":
            <MonitoringContext.Provider
                value={{
                    databaseName,
                    companyId,
                    tagMangerEventsDescLink,
                    databaseName,
                    transactionEvents,
                    setTransactionEvents,
                    setConfigParams,
                    companyDomains,
                    newTransTempVersionAvailable,
                    pushDefaultEcommerceObject: handleTransTempsPushDefEcomObj,
                    cleanJsonObject: handleTransTempsCleanObj,
                    handleEventAdd: handleTransTempEventAdd,
                    handleEventRemove: handleTransTempEventRemove,
                    error,
                }}>
                <MonitoringModal context={MonitoringContext} />
            </MonitoringContext.Provider>,

        "email_notif":
            <EmailsNotifContext.Provider value={{
                emailNotifImplementationLink,
                enableEmailNotif: currentConfig?.email_updates || false,
                curEmailsList: currentConfig?.emails?.split(";") || [],
                setConfigParams,
            }}>
                <EmailsNotifModal context={EmailsNotifContext} />
            </EmailsNotifContext.Provider>,
        "slack_notif":
            <SlackNotifContext.Provider value={{
                slackNotifImplementationLink,
                enableSlackNotif: currentConfig?.slack_updates || false,
                slackChannel: currentConfig?.slack_channel || "",
                slackWorkspaceId: currentConfig?.slack_workspace_id || "",
                slackWebhook: slackWebhook || "",
                setConfigParams,
            }}>
                <SlackNotifModal context={SlackNotifContext} />
            </SlackNotifContext.Provider>,
        "teams_notif":
            <TeamsNotifContext.Provider value={{
                teamsNotifImplementationLink,
                enableTeamsNotif: currentConfig?.teams_updates || false,
                teamsChannel: currentConfig?.teams_channel || "",
                teamsWebhook: currentConfig?.teams_webhook || "",
                setConfigParams,
            }}>
                <TeamsNotifModal context={TeamsNotifContext} />
            </TeamsNotifContext.Provider>,
    }

    return (
        <>
            <SideMenu
                key={modalContentCodeControl}
                globalState={sideMenuState}
                setGlobalState={setSideMenuState}
                modalContent={currentModal}
                popoverMessage={<>No changes to save.</>}
                saveFunction={() => SaveConfigData({
                    companyId,
                    currentConfig,
                    setCurrentConfig,
                    configParams,
                    modalSaveFunction: ConfigService.saveDataLayerGuardConfigData,
                    handlePanelClick,
                    setLoadingActive,
                    currentPage,
                    setCurrentPage,
                    setModalContentCodeControl,
                    setSideMenuState,
                    success,
                    error,
                    warning,
                })}
                modalWidth={modalWidth}
            />

            <ScenariosContext.Provider
                value={{
                    companyId,
                    configParams,
                    setCurrentConfig,
                    setConfigParams,
                    pageScenariosDescLink,
                    scenariosList: JSON.parse(decodeURIComponent(currentConfig?.scenarios || "") || "[]"),

                    drawerGlobalState: scenariosMenuState,
                    setDrawerGlobalState: setScenariosMenuState,

                    defaultEventTemplates,
                    simplifyDLGEventJson,
                    transformDataObject,

                    error,

                    saveFunction: () => SaveConfigData({
                        companyId,
                        currentConfig,
                        setCurrentConfig,
                        configParams,
                        modalSaveFunction: ConfigService.saveDataLayerGuardConfigData,
                        handlePanelClick,
                        setLoadingActive,
                        currentPage,
                        setCurrentPage,
                        setModalContentCodeControl,
                        setSideMenuState,
                        success,
                        error,
                        warning,
                    })
                }}>
                <ScenariosMenu context={ScenariosContext} />
            </ScenariosContext.Provider>

            <div className="row">
                <LoadingScreen isActive={loadingActive} />
                <div className="col-12 col-sm-10 col-md-8 text-start">
                    <div>
                        <div className="row">
                            <PageHeader 
                                categoryName="configuration" 
                                pageName="DataLayer Guard" />
                            <div className="container-rows mt-4">
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="36" height="36" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="19.084" cy="18.8315" r="8.81877" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M13.2442 9.92664C13.1578 9.67701 13.2788 9.40304 13.5215 9.2988L15.4641 8.4645C15.696 8.36491 15.9658 8.4522 16.0954 8.66875L16.7805 9.81351L13.6212 11.0161L13.2442 9.92664Z" fill="#C78FFF"/>
                                                <circle cx="18.701" cy="18.8962" r="8.81877" transform="rotate(32.5681 18.701 18.8962)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M18.2164 8.1938C18.2521 7.93206 18.4838 7.74226 18.7474 7.75885L20.8574 7.89163C21.1093 7.90748 21.3106 8.10719 21.3284 8.35892L21.4228 9.68969L18.0609 9.33617L18.2164 8.1938Z" fill="#C78FFF"/>
                                                <circle cx="18.6062" cy="18.7905" r="8.81877" transform="rotate(63.5775 18.6062 18.7905)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M23.68 9.24714C23.8603 9.05408 24.159 9.033 24.3646 9.19884L26.0103 10.5261C26.2067 10.6846 26.2557 10.9639 26.1248 11.1797L25.4331 12.3204L22.8931 10.0898L23.68 9.24714Z" fill="#C78FFF"/>
                                                <circle cx="18.9423" cy="18.7066" r="8.81877" transform="rotate(92.0599 18.9423 18.7066)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M27.637 12.9186C27.8806 12.8164 28.1617 12.9197 28.2813 13.1553L29.238 15.0406C29.3523 15.2656 29.2824 15.5404 29.0746 15.6836L27.9759 16.4405L26.5738 13.3645L27.637 12.9186Z" fill="#C78FFF"/>
                                                <circle cx="18.5756" cy="18.7768" r="8.81877" transform="rotate(125.185 18.5756 18.7768)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M29.1188 18.5309C29.3807 18.5654 29.5715 18.7963 29.5561 19.06L29.4323 21.1706C29.4176 21.4225 29.2187 21.6247 28.9671 21.6436L27.6367 21.7437L27.9758 18.3803L29.1188 18.5309Z" fill="#C78FFF"/>
                                                <circle cx="18.7435" cy="18.4266" r="8.81877" transform="rotate(157.753 18.7435 18.4266)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M27.7249 23.8976C27.9268 24.0679 27.9631 24.3652 27.8079 24.579L26.566 26.29C26.4177 26.4942 26.1413 26.5573 25.9191 26.4376L24.7446 25.8047L26.8433 23.1546L27.7249 23.8976Z" fill="#C78FFF"/>
                                                <circle cx="18.8845" cy="18.4099" r="8.81877" transform="rotate(-171.238 18.8845 18.4099)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M23.9654 27.8133C24.0385 28.0671 23.9031 28.3343 23.6552 28.4255L21.6711 29.1557C21.4343 29.2429 21.1695 29.1414 21.0515 28.9183L20.4281 27.7388L23.6466 26.7053L23.9654 27.8133Z" fill="#C78FFF"/>
                                                <circle cx="18.7594" cy="18.733" r="8.81877" transform="rotate(-142.755 18.7594 18.733)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M18.5276 29.1167C18.4708 29.3746 18.2244 29.5449 17.963 29.5069L15.8708 29.2025C15.6211 29.1662 15.4367 28.9507 15.4394 28.6984L15.4539 27.3643L18.7758 27.9908L18.5276 29.1167Z" fill="#C78FFF"/>
                                                <circle cx="18.878" cy="18.3864" r="8.81877" transform="rotate(-116.774 18.878 18.3864)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <circle cx="19.1082" cy="18.6992" r="8.81877" transform="rotate(-84.2059 19.1082 18.6992)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M9.64457 24.1058C9.3879 24.1682 9.12659 24.0219 9.04573 23.7704L8.39861 21.7577C8.32136 21.5174 8.43375 21.2571 8.66156 21.1485L9.86591 20.5746L10.7648 23.8333L9.64457 24.1058Z" fill="#C78FFF"/>
                                                <circle cx="19.0566" cy="18.8314" r="8.81877" transform="rotate(-53.1965 19.0566 18.8314)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M8.39719 18.5824C8.14555 18.5021 7.99865 18.241 8.06055 17.9842L8.556 15.9289C8.61514 15.6836 8.84663 15.5198 9.09766 15.5457L10.4247 15.6828L9.49548 18.933L8.39719 18.5824Z" fill="#C78FFF"/>
                                                <circle cx="18.8303" cy="18.5692" r="8.81877" transform="rotate(-24.7141 18.8303 18.5692)" stroke="currentColor" strokeWidth="1.51179"/>
                                                <path d="M9.6951 13.5698C9.50671 13.3847 9.49328 13.0854 9.66432 12.8841L11.0333 11.273C11.1967 11.0807 11.4772 11.0389 11.6895 11.1752L12.8122 11.8959L10.5173 14.378L9.6951 13.5698Z" fill="#C78FFF"/>
                                                <rect x="15.6782" y="20.2761" width="3.12115" height="11.5175" rx="1.56057" transform="rotate(39.1635 15.6782 20.2761)" fill="#C78FFF" stroke="currentColor" strokeWidth="0.408355"/>
                                                <path d="M18.0619 15.3727C17.9075 14.794 17.3046 14.4399 16.7741 14.718C16.2837 14.9752 15.8382 15.3151 15.4585 15.7254C14.8319 16.4025 14.41 17.2434 14.2418 18.1506C14.0737 19.0577 14.1661 19.994 14.5085 20.8507C14.8508 21.7075 15.429 22.4496 16.1761 22.9911C16.9231 23.5325 17.8083 23.8511 18.729 23.9099C19.6497 23.9687 20.5683 23.7653 21.3781 23.3232C22.1879 22.8812 22.8558 22.2186 23.3043 21.4124C23.5761 20.9238 23.7606 20.3947 23.8524 19.8486C23.9516 19.258 23.4275 18.7951 22.8295 18.8285L22.1879 18.8643C21.5899 18.8977 21.1559 19.4397 20.8855 19.974C20.8733 19.9981 20.8606 20.0219 20.8475 20.0456C20.6574 20.3873 20.3743 20.6681 20.031 20.8555C19.6878 21.0429 19.2984 21.1291 18.9082 21.1042C18.5179 21.0793 18.1427 20.9442 17.826 20.7147C17.5094 20.4852 17.2643 20.1706 17.1192 19.8075C16.9741 19.4443 16.9349 19.0475 17.0062 18.663C17.0775 18.2785 17.2563 17.922 17.5219 17.635C17.5403 17.6152 17.559 17.5957 17.5781 17.5767C18.0018 17.1534 18.3818 16.5723 18.2275 15.9936L18.0619 15.3727Z" fill="#C78FFF"/>
                                            </svg>
                                        }
                                        headerText="General settings"
                                        descriptionText="Basic settings for the configuration of the DataLayer Guard"
                                        available={true}
                                        clickFunction={() => handlePanelClick("general")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_60_92)">
                                            <path d="M1.25 20.75L3.0625 18.9375M2.08333 16.5833C2.08333 18.4243 3.57572 19.9167 5.41667 19.9167C7.25762 19.9167 8.75 18.4243 8.75 16.5833C8.75 14.7424 7.25762 13.25 5.41667 13.25C3.57572 13.25 2.08333 14.7424 2.08333 16.5833Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </g>
                                            <path d="M3 12V7M10 19H18C19.6569 19 21 17.6569 21 16V7M3 7V4C3 2.34315 4.34315 1 6 1H18C19.6569 1 21 2.34315 21 4V7M3 7H21" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M4 12H2V13C2.5 12.5 3.01558 12.1984 4 12Z" fill="currentColor"/>
                                            <path d="M10 18L10 20L9 20C9.5 19.5 9.80155 18.9844 10 18Z" fill="currentColor"/>
                                            <defs>
                                            <clipPath id="clip0_60_92">
                                            <rect width="10" height="10" fill="white" transform="matrix(-1 0 0 1 10 12)"/>
                                            </clipPath>
                                            </defs>
                                            </svg>
                                        }
                                        headerText="Monitoring via scraped events"
                                        descriptionText="Add, edit or remove dataLayer events for monitoring"
                                        available={true}
                                        clickFunction={() => handlePanelClick("dl_events")}
                                    />
                                </div>                               
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M8.5 7.96533C8.5 7.48805 8.5 7.24941 8.59974 7.11618C8.68666 7.00007 8.81971 6.92744 8.96438 6.9171C9.13038 6.90525 9.33112 7.03429 9.73261 7.29239L14.4532 10.3271C14.8016 10.551 14.9758 10.663 15.0359 10.8054C15.0885 10.9298 15.0885 11.0702 15.0359 11.1946C14.9758 11.337 14.8016 11.449 14.4532 11.6729L9.73261 14.7076C9.33112 14.9657 9.13038 15.0948 8.96438 15.0829C8.81971 15.0726 8.68666 14.9999 8.59974 14.8838C8.5 14.7506 8.5 14.512 8.5 14.0347V7.96533Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        }
                                        headerText="Monitoring via page scenarios"
                                        descriptionText="Collecting your dataLayer events using interaction scenarios"
                                        available={true}
                                        clickFunction={() => handlePanelClick("scenarios")}
                                    />
                                </div>                                                              
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="21" height="21" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14.6745 23.9763L10.335 19.7448L19.6547 10.2278L24.1477 14.6312L14.6745 23.9763Z" fill="#D4F58A"/>
                                                <path d="M14.6927 5.26754L10.3688 0.852783L0.914075 10.2178C-0.304692 11.4366 -0.304692 13.4124 0.914075 14.6326L10.2267 24.0061L14.6302 19.8868L7.53491 12.4252L14.6927 5.26754Z" fill="#A3CC47"/>
                                                <path d="M24.0844 10.2901L14.7092 0.915125C13.4891 -0.305042 11.5103 -0.305042 10.2901 0.915125C9.06995 2.13529 9.06995 4.11398 10.2901 5.33415L19.6653 14.7091C20.8855 15.9293 22.8642 15.9293 24.0844 14.7091C25.3046 13.4889 25.3046 11.5103 24.0844 10.2901Z" fill="#D4F58A"/>
                                                <path d="M12.428 24.9995C14.1147 24.9995 15.4821 23.6322 15.4821 21.9456C15.4821 20.2589 14.1147 18.8916 12.428 18.8916C10.7414 18.8916 9.37402 20.2589 9.37402 21.9456C9.37402 23.6322 10.7414 24.9995 12.428 24.9995Z" fill="#A3CC47"/>
                                            </svg>
                                        }
                                        headerText="Monitoring via Tag Manager"
                                        descriptionText="Set-up monitoring via Tag Manager to for example monitor purchases"
                                        available={true}
                                        clickFunction={() => handlePanelClick("monitoring")}
                                    />
                                </div>
                            </div>
                            <h3 style={{ fontSize: '1em' }} className="page-category">Notification settings</h3>
                            <div className="container-rows">
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="29" height="23" viewBox="0 0 29 23" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#C78FFF' }}>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M27.968 13.0457L16.0391 0.549338C15.9538 0.459976 15.8519 0.395966 15.7429 0.357527L16.4113 12.5273L27.968 13.0457ZM15.7428 13.4342L15.5218 13.5274L15.2737 9.01009L7.67345 8.83352L19.8475 21.5868C19.9923 21.7385 20.1849 21.8172 20.3792 21.8217L20.5691 13.6508L15.7428 13.4342ZM21.5683 13.6956L21.3949 21.1596L28.3373 14.5325C28.4867 14.3899 28.5652 14.201 28.5719 14.0098L21.5683 13.6956ZM7.38776 7.82662L15.2187 8.00854L14.8155 0.667481L7.54934 7.60364C7.48032 7.66952 7.42643 7.74529 7.38776 7.82662Z" fill="currentColor"/>
                                            <line x1="0.284792" y1="6.79077" x2="1.99354" y2="6.79077" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="3.32257" y1="6.79077" x2="5.03132" y2="6.79077" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="0.284792" y1="11.7272" x2="3.51243" y2="11.7272" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="4.46173" y1="11.7272" x2="4.65159" y2="11.7272" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="3.32257" y1="14.3853" x2="6.55021" y2="14.3853" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="1.42395" y1="17.423" x2="8.82854" y2="17.423" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            </svg>
                                        }
                                        headerText="Email notifications"
                                        descriptionText="Configure settings to receive notifications per mail"
                                        available={emailNotifAvailable}
                                        clickFunction={() => handlePanelClick("email_notif")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<Slack size={21} />}
                                        headerText="Slack notifications"
                                        descriptionText="Configure settings to receive notifications via Slack"
                                        available={slackNotifAvailable}
                                        clickFunction={() => handlePanelClick("slack_notif")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<MicrosoftTeams size={21} />}
                                        headerText="Teams notifications"
                                        descriptionText="Configure settings to receive notifications via Teams"
                                        available={teamsNotifAvailable}
                                        clickFunction={() => handlePanelClick("teams_notif")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
